import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  HiOutlineUser,
  HiOutlineShoppingBag,
  HiOutlineHeart,
  HiOutlineLogout,
  HiOutlineChevronRight,
} from 'react-icons/hi';
import { onSignOutSuccess } from '../../../store/auth/sessionSlice';

/**
 * Premium account dropdown for the new UI.
 * - Circular avatar button with user initials on the header surface.
 * - Dropdown panel with a short user identity block at the top, a clean
 *   menu list below, and a divided logout action.
 * - Closes on outside click, Escape key, and route change.
 */
const UserDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  const initials = getInitials(user?.fullname);
  const secondary = user?.phone || user?.email || '';

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const handleLogout = () => {
    dispatch(onSignOutSuccess());
    navigate('/');
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <style>{`
        @keyframes udFade {
          from { opacity: 0; transform: translateY(-6px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .ud-panel { animation: udFade 0.2s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
        .ud-item { transition: background-color 0.15s, color 0.15s; }
        .ud-item:hover { background: var(--ui-surface-2); }
      `}</style>

      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-label="Account menu"
        className="grid place-items-center font-extrabold transition-transform hover:-translate-y-[1px]"
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: 'var(--accent-soft, var(--site-color-10))',
          color: 'var(--accent, var(--site-color))',
          border: '1px solid var(--ui-line-2)',
          fontSize: '14px',
          letterSpacing: '-0.01em',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {initials || <HiOutlineUser size={16} />}
      </button>

      {isOpen && (
        <div
          className="ud-panel absolute right-0 origin-top-right"
          style={{
            top: 'calc(100% + 8px)',
            width: '280px',
            background: 'var(--ui-surface)',
            borderRadius: '16px',
            border: '1px solid var(--ui-line)',
            boxShadow:
              '0 24px 60px -16px rgba(15, 23, 42, 0.2), 0 4px 12px rgba(15, 23, 42, 0.06)',
            fontFamily: 'Geist, system-ui, sans-serif',
            zIndex: 50,
            overflow: 'hidden',
          }}
        >
          {/* Identity block */}
          <div
            className="flex items-center gap-3"
            style={{
              padding: '14px 16px',
              borderBottom: '1px solid var(--ui-line)',
            }}
          >
            <div
              className="grid place-items-center font-extrabold flex-shrink-0"
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                background: 'var(--accent-soft, var(--site-color-10))',
                color: 'var(--accent, var(--site-color))',
                fontSize: '15px',
                letterSpacing: '-0.02em',
              }}
            >
              {initials || <HiOutlineUser size={18} />}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="truncate"
                style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  color: 'var(--ui-ink)',
                  lineHeight: 1.2,
                }}
              >
                {user?.fullname || 'Welcome'}
              </div>
              {secondary && (
                <div
                  className="truncate"
                  style={{
                    fontSize: '12px',
                    color: 'var(--ui-ink-3)',
                    marginTop: '2px',
                    fontFamily: user?.phone
                      ? 'Geist Mono, monospace'
                      : 'inherit',
                    letterSpacing: user?.phone ? '0.02em' : 'normal',
                  }}
                >
                  {secondary}
                </div>
              )}
            </div>
          </div>

          {/* Menu items */}
          <div style={{ padding: '6px' }}>
            <DropdownItem
              to="/user/profile"
              icon={<HiOutlineUser size={17} />}
              label="Profile"
              onNavigate={() => setIsOpen(false)}
            />
            <DropdownItem
              to="/user/orders"
              icon={<HiOutlineShoppingBag size={17} />}
              label="Orders"
              onNavigate={() => setIsOpen(false)}
            />
            <DropdownItem
              to="/user/favorites"
              icon={<HiOutlineHeart size={17} />}
              label="Favorites"
              onNavigate={() => setIsOpen(false)}
            />
          </div>

          {/* Logout */}
          <div
            style={{
              padding: '6px',
              borderTop: '1px solid var(--ui-line)',
            }}
          >
            <button
              type="button"
              onClick={handleLogout}
              className="ud-item w-full inline-flex items-center gap-3"
              style={{
                padding: '9px 10px',
                borderRadius: '10px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                color: '#B91C1C',
              }}
            >
              <HiOutlineLogout size={17} />
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                }}
              >
                Log out
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const DropdownItem = ({ to, icon, label, onNavigate }) => (
  <Link
    to={to}
    onClick={onNavigate}
    className="ud-item w-full inline-flex items-center gap-3"
    style={{
      padding: '9px 10px',
      borderRadius: '10px',
      textDecoration: 'none',
      color: 'var(--ui-ink)',
    }}
  >
    <span style={{ color: 'var(--ui-ink-3)' }}>{icon}</span>
    <span
      className="flex-1"
      style={{
        fontSize: '14px',
        fontWeight: 600,
        letterSpacing: '-0.01em',
      }}
    >
      {label}
    </span>
    <HiOutlineChevronRight
      size={14}
      style={{ color: 'var(--ui-ink-3)', opacity: 0.6 }}
    />
  </Link>
);

function getInitials(name) {
  if (!name) return '';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

export default UserDropdown;
